import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CreditCardIcon,
  BanknotesIcon,
  XMarkIcon,
  CheckCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline'
import UnifiedOrderService from '../../services/unifiedOrderService'
import customerService from '../../services/customerService'
import realtimeService from '../../services/realtimeService'
import useCartStore from '../../stores/useCartStore'
import { supabase } from '../../config/supabase'
import toast from 'react-hot-toast'
import offerService from '../../services/offerService'
import { getDeviceId, validatePhoneNumber } from '../../utils/deviceFingerprint'
import { createOrderWithTracking } from '../../services/deviceTrackingService'
import CashfreePaymentService from '../../services/cashfreePaymentService'

// Ordyrr Brand Colors
const BRAND_GREEN = '#00E676'
const ACTION_GREEN = '#00C853'
const DARK_TEXT = '#212121'
const MEDIUM_GRAY = '#666666'

const CheckoutModal = ({ isOpen, onClose, onSuccess, restaurantId, tableId, sessionId, currentCustomer, initialTip = 0, initialUseOrdyrrCoins = false, appliedOffer = null }) => {
  const [loading, setLoading] = useState(false)
  const [selectedTip, setSelectedTip] = useState(initialTip)
  const [orderSuccess, setOrderSuccess] = useState(false)
  const [orderData, setOrderData] = useState(null)
  const [selectedPayment, setSelectedPayment] = useState('cash')
  const [processingPayment, setProcessingPayment] = useState(false)
  const [cashfreeLoaded, setCashfreeLoaded] = useState(false)
  const [showDiscountPopup, setShowDiscountPopup] = useState(false)
  const [discountAmount, setDiscountAmount] = useState(0)
  const [hasUsedFirstOrderDiscount, setHasUsedFirstOrderDiscount] = useState(false)
  const [currentOffer, setCurrentOffer] = useState(null)
  const [useOrdyrrCoins, setUseOrdyrrCoins] = useState(initialUseOrdyrrCoins) // Use prop value from CartSidebar
  const [availableCoins, setAvailableCoins] = useState(0)
  const hasShownPopup = useRef(false)
  const popupTimeout = useRef(null)
  const [phoneValidation, setPhoneValidation] = useState({ isValid: true, isSuspicious: false, reason: null })

  // Use cart store instead of separate cart service
  const { cart, getCartTotal, getCartWithTax, clearCart } = useCartStore()

  const [customerInfo, setCustomerInfo] = useState({
    name: currentCustomer?.name || '',
    phone: currentCustomer?.phone || '',
    email: currentCustomer?.email || '',
    paymentMethod: 'cash',
    specialInstructions: ''
  })

  // Load Cashfree SDK
  useEffect(() => {
    const loadCashfreeSDK = () => {
      if (window.Cashfree) {
        console.log('✅ Cashfree SDK already loaded')
        setCashfreeLoaded(true)
        return
      }

      const script = document.createElement('script')
      script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js'
      script.async = true
      script.onload = () => {
        console.log('✅ Cashfree SDK loaded successfully')
        setCashfreeLoaded(true)
      }
      script.onerror = () => {
        console.error('❌ Failed to load Cashfree SDK')
        toast.error('Payment gateway unavailable. Please try cash payment.')
      }
      document.body.appendChild(script)
    }

    loadCashfreeSDK()
  }, [])

  // Update selectedTip when initialTip changes
  React.useEffect(() => {
    setSelectedTip(initialTip)
  }, [initialTip])

  // Update customer info when currentCustomer changes
  React.useEffect(() => {
    if (currentCustomer) {
      setCustomerInfo(prev => ({
        ...prev,
        name: currentCustomer.name || '',
        phone: currentCustomer.phone || '',
        email: currentCustomer.email || ''
      }))
    }
  }, [currentCustomer])

  // Auto-fill customer info for returning guests (device tracking)
  React.useEffect(() => {
    const loadGuestCustomerData = async () => {
      // Skip if user is logged in
      if (currentCustomer) return

      try {
        const deviceId = getDeviceId()
        if (!deviceId) return

        console.log('🔍 [CheckoutModal] Checking for returning guest...', deviceId)

        // Query device_tracking to find linked customer
        const { data: deviceData, error: deviceError } = await supabase
          .from('device_tracking')
          .select('linked_customer_id')
          .eq('device_fingerprint', deviceId)
          .maybeSingle()

        if (deviceError || !deviceData?.linked_customer_id) {
          console.log('ℹ️ [CheckoutModal] No linked customer found')
          return
        }

        // Fetch customer details
        const { data: customerData, error: customerError } = await supabase
          .from('customers')
          .select('full_name, phone, email')
          .eq('id', deviceData.linked_customer_id)
          .single()

        if (!customerError && customerData) {
          console.log('✅ [CheckoutModal] Auto-filling returning guest info:', customerData.full_name)
          setCustomerInfo(prev => ({
            ...prev,
            name: customerData.full_name || prev.name,
            phone: customerData.phone || prev.phone,
            email: customerData.email || prev.email
          }))
          
          // Show a friendly toast
          toast.success(`Welcome back, ${customerData.full_name}! 🎉`, {
            duration: 3000,
            icon: '👋'
          })
        }
      } catch (error) {
        console.error('❌ [CheckoutModal] Error loading guest data:', error)
      }
    }

    loadGuestCustomerData()
  }, [currentCustomer, isOpen])

  const tipAmounts = [10, 20, 30, 50, 100, 200]
  
  // Check if user is logged in
  const isLoggedIn = !!currentCustomer

  // Fetch customer's Ordyrr Coins balance
  React.useEffect(() => {
    const fetchCoinsBalance = async () => {
      if (currentCustomer?.id) {
        try {
          console.log('💰 [CheckoutModal] Fetching GLOBAL coins for customer:', currentCustomer.id)
          const { data, error } = await supabase
            .from('loyalty_points')
            .select('points_earned, points_redeemed')
            .eq('customer_id', currentCustomer.id)
            // Removed restaurant_id filter - coins are now global across all restaurants
          
          if (error) {
            console.error('❌ Error fetching coins:', error)
            return
          }
          
          if (data) {
            // Calculate total points: sum of all earned points minus redeemed points across ALL restaurants
            const totalCoins = (data || []).reduce((total, transaction) => {
              const earned = transaction.points_earned || 0
              const redeemed = transaction.points_redeemed || 0
              return total + earned - redeemed
            }, 0)
            setAvailableCoins(totalCoins)
            console.log('✅ [CheckoutModal] Available GLOBAL Ordyrr Coins:', totalCoins, 'from', data.length, 'transactions across all restaurants')
          }
        } catch (error) {
          console.error('❌ Exception fetching coins balance:', error)
        }
      }
    }
    fetchCoinsBalance()
  }, [currentCustomer])

  // Check if customer has used the first order offer
  React.useEffect(() => {
    const checkOfferEligibility = async () => {
      if (currentCustomer?.id && cart && cart.length > 0) {
        try {
          console.log('🔍 [CheckoutModal] Checking offer eligibility for customer:', currentCustomer.id)
          
          // Calculate subtotal to determine which offer to show
          const currentSubtotal = getCartTotal()
          
          // Fetch both first order offers
          const { data: offers, error: offerError } = await supabase
            .from('offers')
            .select('*')
            .in('offer_code', ['FIRST_ORDER_10', 'FIRST_ORDER_25'])
            .eq('is_active', true)
          
          if (offerError || !offers || offers.length === 0) {
            console.log('❌ [CheckoutModal] First order offers not found or inactive')
            return
          }
          
          console.log('✅ [CheckoutModal] Found offers:', offers.map(o => o.offer_code))
          
          // Determine which offer to show based on subtotal
          // If subtotal >= 499, show FIRST_ORDER_10 (10% off, max ₹50)
          // If subtotal >= 299 and < 499, show FIRST_ORDER_25 (flat ₹25 off)
          const offer10 = offers.find(o => o.offer_code === 'FIRST_ORDER_10')
          const offer25 = offers.find(o => o.offer_code === 'FIRST_ORDER_25')
          
          let selectedOffer = null
          if (currentSubtotal >= 499 && offer10) {
            selectedOffer = offer10
          } else if (currentSubtotal >= 299 && currentSubtotal < 499 && offer25) {
            selectedOffer = offer25
          } else if (currentSubtotal >= 499 && !offer10 && offer25) {
            // Fallback: if FIRST_ORDER_10 doesn't exist but subtotal is high enough
            selectedOffer = offer25
          }
          
          if (selectedOffer) {
            console.log('✅ [CheckoutModal] Selected offer:', selectedOffer.offer_code, 'for subtotal:', currentSubtotal)
            setCurrentOffer(selectedOffer)
          } else {
            console.log('⚠️ [CheckoutModal] No offer matches current subtotal:', currentSubtotal)
            setCurrentOffer(null)
          }
          
          // IMPORTANT: Check if customer has placed ANY order before (not just used the offer)
          // First order discount is ONLY for the actual first order
          const { data: orderHistory, error: orderError } = await supabase
            .from('orders')
            .select('id')
            .eq('customer_id', currentCustomer.id)
            .limit(1)
          
          console.log('📊 [CheckoutModal] Customer order history check:', {
            customer_id: currentCustomer.id,
            hasPlacedOrder: orderHistory && orderHistory.length > 0,
            orderError
          })
          
          if (!orderError && orderHistory && orderHistory.length > 0) {
            // Customer has already placed at least one order
            // First order discount is NO LONGER available
            console.log('🚫 [CheckoutModal] Customer has already placed an order - First order discount not available')
            setHasUsedFirstOrderDiscount(true)
          } else {
            // This is the customer's first order - discount is available
            console.log('✅ [CheckoutModal] Customer is placing their FIRST order - First order discount available')
            setHasUsedFirstOrderDiscount(false)
          }
        } catch (error) {
          console.error('❌ [CheckoutModal] Error checking offer eligibility:', error)
        }
      }
    }
    checkOfferEligibility()
  }, [currentCustomer, cart, getCartTotal])

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (popupTimeout.current) {
        clearTimeout(popupTimeout.current)
      }
    }
  }, [])

  // Reset popup flag when modal opens
  React.useEffect(() => {
    if (isOpen) {
      hasShownPopup.current = false
    }
  }, [isOpen])

  // Cart summary with automatic discount calculation
  // IMPORTANT: Initialize with safe defaults to prevent "can't read before initializing" errors
  const cartSummary = React.useMemo(() => {
    // Always return a valid object with all properties to prevent initialization errors
    if (!cart || cart.length === 0) {
      return { 
        isEmpty: true, 
        subtotal: 0, 
        total: 0, 
        discount: 0, 
        coinsDiscount: 0, 
        coinsUsed: 0,
        itemSavings: 0,
        items: [] 
      }
    }
    
    const subtotal = getCartTotal()
    
    // Calculate savings from menu item discounts
    // Savings = menu_display_price (marked up) - platform_price (actual selling price)
    const itemSavings = cart.reduce((savings, item) => {
      const displayPrice = item.menu_display_price // Higher marked-up price
      const sellingPrice = item.platform_price // Actual selling price
      
      if (displayPrice && sellingPrice && displayPrice > sellingPrice) {
        const itemSaving = (displayPrice - sellingPrice) * item.quantity
        return savings + itemSaving
      }
      return savings
    }, 0)
    
    // Check if user is authenticated (NOT a guest)
    const isLoggedIn = currentCustomer?.id && !currentCustomer?.isGuest
    
    console.log('💰 CheckoutModal cartSummary:', {
      subtotal,
      appliedOffer: appliedOffer ? appliedOffer.offer_code : 'none',
      useOrdyrrCoins,
      isLoggedIn,
      isGuest: currentCustomer?.isGuest,
      currentOffer: currentOffer ? currentOffer.offer_code : 'none'
    })
    
    // RULE: Only ONE offer can be applied at a time
    // Priority: Ordyrr Coins > Applied Database Offer > First Order Discount > Other Coupons
    
    let discount = 0
    let coinsDiscount = 0
    let coinsUsed = 0
    
    // Check if Ordyrr Coins are being used (highest priority)
    if (useOrdyrrCoins && isLoggedIn && availableCoins > 0 && subtotal >= 100) {
      // Calculate Ordyrr Coins discount
      // Max discount is 5% of subtotal
      const maxCoinsDiscount = subtotal * 0.05
      // Max 500 coins per order (₹50)
      const maxCoinsAllowed = 500
      // Use exact number of coins available (up to max allowed)
      coinsUsed = Math.min(availableCoins, maxCoinsAllowed)
      // Convert coins to exact rupee value (1 coin = ₹0.10, so 10 coins = ₹1)
      const coinsValue = coinsUsed / 10
      // Take the minimum of coins value and max allowed discount (5%)
      coinsDiscount = Math.min(coinsValue, maxCoinsDiscount)
      // Recalculate exact coins used based on final discount (no rounding)
      coinsUsed = coinsDiscount * 10
      
      // If using Ordyrr Coins, NO other offers can be applied
      discount = 0
    } else if (appliedOffer && subtotal >= (appliedOffer.min_order_amount || 0)) {
      // Calculate discount from applied database offer (second priority)
      console.log('💰 CheckoutModal: Calculating discount for applied offer:', appliedOffer.offer_code, 'subtotal:', subtotal)
      discount = offerService.calculateDiscount(appliedOffer, subtotal)
      console.log('💰 CheckoutModal: Calculated discount:', discount)
      
      // If using database offer, NO Ordyrr Coins can be applied
      coinsDiscount = 0
      coinsUsed = 0
    } else if (isLoggedIn && !hasUsedFirstOrderDiscount && currentOffer && subtotal >= (currentOffer.min_order_amount || 0)) {
      // Calculate First Order Discount (only if NOT using Ordyrr Coins or database offer)
      if (currentOffer.discount_type === 'percentage') {
        discount = subtotal * (currentOffer.discount_value / 100)
        // Apply max discount cap if specified
        if (currentOffer.max_discount_amount) {
          discount = Math.min(discount, currentOffer.max_discount_amount)
        }
      } else if (currentOffer.discount_type === 'fixed') {
        discount = currentOffer.discount_value
      }
      
      // If using First Order Discount, NO Ordyrr Coins can be applied
      coinsDiscount = 0
      coinsUsed = 0
    }
    
    const total = subtotal - discount - coinsDiscount
    
    return {
      isEmpty: false,
      subtotal,
      discount,
      coinsDiscount,
      coinsUsed,
      itemSavings,
      total,
      items: cart
    }
  }, [cart, getCartTotal, currentCustomer, hasUsedFirstOrderDiscount, currentOffer, useOrdyrrCoins, availableCoins, appliedOffer])

  // Show celebratory popup when discount is applied
  React.useEffect(() => {
    const discount = cartSummary?.discount || 0
    const isLoggedIn = currentCustomer?.id && !currentCustomer?.isGuest
    if (discount > 0 && !hasShownPopup.current && isOpen && isLoggedIn) {
      hasShownPopup.current = true
      setDiscountAmount(discount)
      setShowDiscountPopup(true)
      // Auto-dismiss after 4 seconds
      popupTimeout.current = setTimeout(() => {
        setShowDiscountPopup(false)
      }, 4000)
    }
  }, [cartSummary, isOpen, currentCustomer])

  const handleSubmit = async () => {
    // Check if user is authenticated (NOT a guest)
    const isLoggedIn = currentCustomer?.id && !currentCustomer?.isGuest
    
    // CRITICAL: Ensure cartSummary is initialized before proceeding
    if (!cartSummary || cartSummary.isEmpty) {
      toast.error('Your cart is empty')
      return
    }

    // For logged-in users, use their stored information
    // For guest users, merge customerInfo with selectedPayment
    const finalCustomerInfo = isLoggedIn ? {
      name: currentCustomer?.full_name || currentCustomer?.name || currentCustomer?.customer_name || '',
      phone: currentCustomer?.phone || currentCustomer?.customer_phone || '',
      email: currentCustomer?.email || currentCustomer?.customer_email || '',
      paymentMethod: selectedPayment || 'cash',
      specialInstructions: ''
    } : {
      ...customerInfo,
      paymentMethod: selectedPayment || customerInfo.paymentMethod || 'cash'
    }

    // Validate required fields
    if (!finalCustomerInfo.name) {
      toast.error('Customer name is missing. Please try logging in again.')
      console.error('Missing customer name:', { currentCustomer, finalCustomerInfo })
      return
    }

    // For logged-in users without phone (e.g., Google OAuth users), use email as fallback
    if (isLoggedIn && !finalCustomerInfo.phone) {
      console.warn('⚠️ Logged-in user has no phone number. Using email as identifier.')
      // Use email or a default value - phone is optional for authenticated users
      finalCustomerInfo.phone = finalCustomerInfo.email || ''
    }

    // For guest users, phone is required for order tracking
    if (!isLoggedIn && !finalCustomerInfo.phone) {
      toast.error('Phone number is required for guest checkout.')
      console.error('Missing phone for guest:', { currentCustomer, finalCustomerInfo })
      return
    }

    // Validate phone number for guest users (device tracking)
    if (!isLoggedIn) {
      const validation = validatePhoneNumber(finalCustomerInfo.phone)
      setPhoneValidation(validation)
      
      if (!validation.isValid) {
        toast.error(validation.reason || 'Invalid phone number')
        return
      }
      
      if (validation.isSuspicious) {
        console.warn('⚠️ Suspicious phone number detected:', validation.reason)
        toast.warning('Phone number pattern detected. Order will proceed but may be flagged.', {
          duration: 4000
        })
      }
    }

    if (!cart || cart.length === 0) {
      toast.error('Your cart is empty')
      return
    }

    setLoading(true)

    try {
      console.log('🛒 Starting optimized checkout workflow...')
      console.log('Customer info:', finalCustomerInfo)
      console.log('Payment method:', finalCustomerInfo.paymentMethod)

      // SAFE: Extract discount and coins values from cartSummary with fallbacks
      const discount = cartSummary.discount || 0
      const coinsDiscount = cartSummary.coinsDiscount || 0
      const coinsUsed = cartSummary.coinsUsed || 0

      // Declare orderResult at function scope so it's accessible in catch blocks
      let orderResult = null

      // ============================================
      // ONLINE PAYMENT FLOW - Optimized for Speed
      // ============================================
      if (finalCustomerInfo.paymentMethod === 'online') {
        console.log('💳 Online payment - optimized flow')
        setProcessingPayment(true)
        
        try {
          // Step 1: Create order with pending_payment status (FAST - no session update)
          console.log('📝 Creating order with pending_payment status...')
          
          orderResult = await UnifiedOrderService.createOrder({
            source: 'customer',
            restaurantId: restaurantId,
            tableId: tableId,
            sessionId: sessionId,
            cartItems: cart,
            customerInfo: {
              name: finalCustomerInfo.name,
              phone: finalCustomerInfo.phone,
              email: finalCustomerInfo.email,
              customerId: isLoggedIn ? currentCustomer?.id : null
            },
            specialInstructions: finalCustomerInfo.specialInstructions,
            paymentMethod: 'online',
            paymentStatus: 'pending', // Mark as pending until payment completes
            tipAmount: selectedTip,
            discountAmount: discount + coinsDiscount,
            coinsRedeemed: coinsUsed
          })

          if (!orderResult || !orderResult.id) {
            throw new Error('Failed to create order')
          }

          console.log('✅ Order created:', orderResult.order_number)

          // Step 2: Parallel processing - Record offer usage and coins (NON-BLOCKING)
          const customerId = orderResult.customer_id || currentCustomer?.id || finalCustomerInfo.customerId
          
          // Process offer and coins in parallel (don't wait)
          Promise.all([
            // Record offer usage if applicable
            (async () => {
              if (appliedOffer && discount > 0 && customerId) {
                try {
                  console.log(`🎟️ Recording offer usage for ${appliedOffer.offer_code}...`)
                  await offerService.recordOfferUsage(
                    customerId,
                    appliedOffer.id,
                    orderResult.id,
                    discount
                  )
                  console.log(`✅ Offer usage recorded`)
                } catch (offerError) {
                  console.error('❌ Error recording offer usage:', offerError)
                }
              }
            })(),
            // Deduct Ordyrr Coins if used
            (async () => {
              if (useOrdyrrCoins && coinsUsed > 0 && isLoggedIn && currentCustomer?.id) {
                try {
                  console.log(`🪙 Redeeming ${coinsUsed} Ordyrr Coins...`)
                  
                  const { error: coinsError } = await supabase
                    .from('loyalty_points')
                    .insert({
                      customer_id: currentCustomer.id,
                      restaurant_id: restaurantId,
                      order_id: orderResult.id,
                      points_earned: 0,
                      points_redeemed: coinsUsed,
                      transaction_type: 'redemption',
                      description: `Redeemed ${coinsUsed} coins for ₹${coinsDiscount.toFixed(2)} discount on order #${orderResult.order_number}`
                    })

                  if (coinsError) {
                    console.error('❌ Error redeeming coins:', coinsError)
                  } else {
                    console.log('✅ Coins redeemed')
                  }
                } catch (coinsError) {
                  console.error('❌ Error in coins redemption:', coinsError)
                }
              }
            })()
          ]).catch(err => console.warn('⚠️ Background processing error:', err))
          
          // Step 3: Create Cashfree payment session immediately (FAST)
          // Ensure phone number is valid for Cashfree (10 digits required)
          let validPhone = finalCustomerInfo.phone
          
          // If phone is empty, email, or invalid format, use a placeholder
          if (!validPhone || validPhone.includes('@') || validPhone.length < 10) {
            validPhone = '9999999999' // Placeholder for Google OAuth users without phone
            console.warn('⚠️ Using placeholder phone for Cashfree (Google OAuth user without phone)')
          }
          
          const paymentSessionData = {
            order_id: orderResult.id, // Pass the order ID
            amount: cartSummary.total + selectedTip,
            customer_name: finalCustomerInfo.name,
            customer_phone: validPhone,
            customer_email: finalCustomerInfo.email || `${finalCustomerInfo.phone}@ordyrr.com`,
            order_note: `Order ${orderResult.order_number} - Table ${tableId} - ${cart.length} items`,
            return_url: `${window.location.origin}/payment-status?order_id=${orderResult.id}`,
            session_id: sessionId,
            restaurant_id: restaurantId,
            table_id: tableId
          }

          console.log('📤 Creating Cashfree payment session', { 
            phone: validPhone, 
            email: paymentSessionData.customer_email 
          })

          // Call backend-proxy to create payment session with reduced timeout for faster failure
          const controller = new AbortController()
          const timeoutId = setTimeout(() => controller.abort(), 15000) // 15 second timeout (reduced from 30s)
          
          let response
          try {
            response = await fetch(`${import.meta.env.VITE_BACKEND_PROXY_URL}/api/proxy/cashfree/create-order`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(paymentSessionData),
              signal: controller.signal
            })
            clearTimeout(timeoutId)
          } catch (fetchError) {
            clearTimeout(timeoutId)
            if (fetchError.name === 'AbortError') {
              throw new Error('Payment request timed out. Please check your internet connection and try again.')
            }
            throw new Error('Unable to connect to payment server. Please check your internet connection.')
          }

          if (!response.ok) {
            let errorMessage = 'Failed to create payment session'
            
            // Handle specific HTTP status codes
            if (response.status === 504) {
              errorMessage = 'Payment gateway is temporarily unavailable. Please try again in a moment or use counter payment.'
            } else if (response.status === 503) {
              errorMessage = 'Payment service is temporarily down. Please use counter payment.'
            } else if (response.status >= 500) {
              errorMessage = 'Payment server error. Please try again or use counter payment.'
            } else {
              // Try to get error message from response
              try {
                const errorData = await response.json()
                errorMessage = errorData.error || errorData.message || errorMessage
              } catch (e) {
                // If response is not JSON, use status-based message
                errorMessage = `Payment request failed (${response.status}). Please try again or use counter payment.`
              }
            }
            throw new Error(errorMessage)
          }

          const paymentResult = await response.json()
          
          if (!paymentResult.success) {
            throw new Error(paymentResult.error || 'Failed to create payment session')
          }
          
          console.log('✅ Payment session created:', paymentResult.data.payment_session_id)
          
          // Step 5: Open Cashfree checkout
          if (!window.Cashfree) {
            throw new Error('Cashfree SDK not loaded')
          }
          
          const cashfree = window.Cashfree({
            mode: import.meta.env.VITE_CASHFREE_ENVIRONMENT === 'production' ? 'production' : 'sandbox'
          })
          
          // Store order ID in localStorage (persists across redirects)
          console.log('💾 Storing order ID in localStorage:', orderResult.id)
          localStorage.setItem('pending_order_id', orderResult.id)
          localStorage.setItem('pending_restaurant_id', restaurantId)
          localStorage.setItem('pending_table_id', tableId)
          localStorage.setItem('pending_order_timestamp', Date.now().toString())
          console.log('✅ LocalStorage set:', {
            pending_order_id: localStorage.getItem('pending_order_id'),
            pending_restaurant_id: localStorage.getItem('pending_restaurant_id'),
            pending_table_id: localStorage.getItem('pending_table_id')
          })
          
          const returnUrl = `${window.location.origin}/payment-status?order_id=${orderResult.id}`
          console.log('🔗 Return URL:', returnUrl)
          
          // Clear cart before opening payment
          clearCart()
          
          // Store order info for manual return
          localStorage.setItem('pending_payment_order_id', orderResult.id)
          localStorage.setItem('pending_payment_return_url', returnUrl)
          
          // Use Cashfree SDK checkout
          console.log('🚀 Opening Cashfree checkout with session ID:', paymentResult.data.payment_session_id)
          
          const checkoutOptions = {
            paymentSessionId: paymentResult.data.payment_session_id,
            returnUrl: returnUrl,
            redirectTarget: '_self'
          }
          
          // Show instruction to user
          toast.success('Redirecting to payment gateway...\n\nNote: If payment fails, use the back button to return.', {
            icon: '💳',
            duration: 3000,
            style: {
              minWidth: '300px'
            }
          })
          
          // Open Cashfree checkout
          await cashfree.checkout(checkoutOptions)
          
          // Payment modal opened successfully
          console.log('✅ Cashfree checkout opened - waiting for payment completion')
          toast.success('Redirecting to payment gateway...', {
            icon: '💳',
            duration: 2000
          })
          
          // Close modal - user will complete payment and be redirected
          setTimeout(() => {
            onClose()
          }, 1000)
          
          return // Exit early for online payment
          
        } catch (paymentError) {
          console.error('❌ Payment initiation failed:', paymentError)
          
          // If order was created but payment failed, mark order as cancelled
          if (orderResult && orderResult.id) {
            try {
              console.log('⚠️ Marking order as cancelled due to payment failure...')
              await supabase
                .from('orders')
                .update({ 
                  payment_status: 'failed',
                  status: 'cancelled',
                  special_instructions: `Payment initiation failed: ${paymentError.message}`
                })
                .eq('id', orderResult.id)
              
              console.log('✅ Order marked as cancelled')
            } catch (updateError) {
              console.error('❌ Error updating order status:', updateError)
            }
          }
          
          toast.error(paymentError.message || 'Payment failed. Please try again or use cash payment.')
          setProcessingPayment(false)
          setLoading(false)
          return
        }
      }

      // ============================================
      // CASH PAYMENT FLOW - Optimized for Speed
      // ============================================
      console.log('💵 Cash payment - optimized flow')

      // For guest users, prepare device tracking data (NON-BLOCKING)
      if (!isLoggedIn) {
        console.log('👤 Guest user - preparing device tracking')
        
        const { orderData: enhancedOrderData, customer, applicableOffers, phoneValidation: phoneCheck } = 
          await createOrderWithTracking(
            {
              source: 'customer',
              restaurantId,
              tableId,
              sessionId,
              cartItems: cart,
              specialInstructions: finalCustomerInfo.specialInstructions,
              paymentMethod: finalCustomerInfo.paymentMethod,
              tipAmount: selectedTip,
              discountAmount: cartSummary.discount || 0,
              coinsRedeemed: 0, // Guests can't use coins
              firstOrderDiscountApplied: false
            },
            finalCustomerInfo
          )
        
        console.log('✅ Device tracking prepared')
        finalCustomerInfo.customerId = customer.id
      }

      // Create order immediately (FAST - no session update)
      orderResult = await UnifiedOrderService.createOrder({
        source: 'customer',
        restaurantId,
        tableId,
        sessionId,
        cartItems: cart,
        customerInfo: {
          name: finalCustomerInfo.name,
          phone: finalCustomerInfo.phone,
          email: finalCustomerInfo.email,
          customerId: isLoggedIn ? currentCustomer?.id : null
        },
        specialInstructions: finalCustomerInfo.specialInstructions,
        paymentMethod: finalCustomerInfo.paymentMethod,
        tipAmount: selectedTip,
        discountAmount: discount + coinsDiscount,
        coinsRedeemed: coinsUsed
      })

      if (!orderResult || !orderResult.id) {
        throw new Error('Failed to create order')
      }

      console.log('✅ Order created:', orderResult.order_number)
      
      // Process offer and coins in parallel (NON-BLOCKING - don't wait)
      const customerId = orderResult.customer_id || currentCustomer?.id || finalCustomerInfo.customerId
      
      Promise.all([
        // Record offer usage if applicable
        (async () => {
          if (appliedOffer && discount > 0 && customerId) {
            try {
              console.log(`🎟️ Recording offer usage for ${appliedOffer.offer_code}...`)
              await offerService.recordOfferUsage(
                customerId,
                appliedOffer.id,
                orderResult.id,
                discount
              )
              console.log(`✅ Offer usage recorded`)
            } catch (offerError) {
              console.error('❌ Error recording offer usage:', offerError)
            }
          }
        })(),
        // Deduct Ordyrr Coins if used
        (async () => {
          if (useOrdyrrCoins && coinsUsed > 0 && isLoggedIn && currentCustomer?.id) {
            try {
              console.log(`🪙 Redeeming ${coinsUsed} Ordyrr Coins...`)
              
              const { error: coinsError } = await supabase
                .from('loyalty_points')
                .insert({
                  customer_id: currentCustomer.id,
                  restaurant_id: restaurantId,
                  order_id: orderResult.id,
                  points_earned: 0,
                  points_redeemed: coinsUsed,
                  transaction_type: 'redemption',
                  description: `Redeemed ${coinsUsed} coins for ₹${coinsDiscount.toFixed(2)} discount on order #${orderResult.order_number}`
                })

              if (coinsError) {
                console.error('❌ Error redeeming coins:', coinsError)
                toast.error('Coins could not be redeemed. Please contact support.', {
                  duration: 5000
                })
              } else {
                console.log(`✅ Coins redeemed`)
                toast.success(`🪙 ${coinsUsed} coins redeemed! You saved ₹${coinsDiscount.toFixed(2)}`, {
                  duration: 3000
                })
              }
            } catch (coinsError) {
              console.error('❌ Exception redeeming coins:', coinsError)
            }
          }
        })()
      ]).catch(err => console.warn('⚠️ Background processing error:', err))
      
      // Clear cart immediately and show success (FAST)
      clearCart()
      
      setOrderData({
        ...orderResult,
        customerInfo,
        total: cartSummary.total + selectedTip,
        paymentMethod: customerInfo.paymentMethod
      })
      setOrderSuccess(true)

      if (customerInfo.paymentMethod === 'cash') {
        toast.success(`Order #${orderResult.order_number} placed!`, {
          icon: '🛎️',
          duration: 3000
        })
      } else {
        toast.success(`Order #${orderResult.order_number} placed successfully!`, {
          icon: '✅',
          duration: 4000
        })
      }

      // Step 6: Start real-time order tracking
      try {
        realtimeService.subscribeToOrderUpdates(orderResult.id, {
          onStatusUpdate: (data) => {
            toast.success(`Order status updated: ${data.status}`, {
              icon: '📋'
            })
          },
          onStaffAssigned: (data) => {
            toast.success(`Order assigned to staff member`, {
              icon: '👨‍🍳'
            })
          }
        })
      } catch (realtimeError) {
        console.warn('Real-time tracking setup failed:', realtimeError)
        // Continue without real-time updates
      }

      // Call success callback with order data including customer info
      if (onSuccess) {
        onSuccess({
          ...orderResult,
          customerInfo: finalCustomerInfo
        })
      }
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        onClose()
        setOrderSuccess(false)
        setOrderData(null)
      }, 3000)

    } catch (error) {
      console.error('❌ Complete checkout workflow failed:', error)
      toast.error(error.message || 'Failed to place order. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const updateCustomerSession = async () => {
    try {
      // Update customer session with customer details
      await supabase
        .from('customer_sessions')
        .update({
          customer_name: customerInfo.name,
          customer_phone: customerInfo.phone,
          updated_at: new Date().toISOString()
        })
        .eq('session_id', sessionId)
    } catch (error) {
      console.warn('Failed to update customer session:', error)
    }
  }

  // Calculate totals from cart summary
  const subtotal = cartSummary?.subtotal || 0
  const discount = cartSummary?.discount || 0
  const coinsDiscount = cartSummary?.coinsDiscount || 0
  const coinsUsed = cartSummary?.coinsUsed || 0
  const itemSavings = cartSummary?.itemSavings || 0
  const total = cartSummary?.total || 0

  if (!isOpen) return null

  // Show success screen if order was placed
  if (orderSuccess && orderData) {
    const isOnlinePayment = orderData.paymentMethod === 'online'
    const isCashPayment = orderData.paymentMethod === 'cash'
    
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-xl p-6 w-full max-w-lg text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </motion.div>
            
            <h3 className="text-xl font-bold text-black mb-2">
              {isOnlinePayment ? 'Order Placed & Paid!' : 'Order Placed Successfully!'}
            </h3>
            
            <p className="text-gray-600 mb-4">
              Order #{orderData.order_number} has been placed and assigned to our staff.
            </p>
            
            {/* Order Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Order Number</span>
                <span className="font-bold text-black">#{orderData.order_number}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Amount</span>
                <span className="font-bold text-lg text-black">₹{orderData.total.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Payment Method</span>
                <div className="flex items-center gap-2">
                  {isOnlinePayment ? (
                    <>
                      <CreditCardIcon className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-600">Paid Online</span>
                    </>
                  ) : (
                    <>
                      <BanknotesIcon className="h-4 w-4 text-orange-600" />
                      <span className="text-sm font-medium text-orange-600">Pay with Cash</span>
                    </>
                  )}
                </div>
              </div>
              
              {orderData.assigned_staff_name && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Assigned Staff</span>
                  <span className="text-sm font-medium text-black">{orderData.assigned_staff_name}</span>
                </div>
              )}
            </div>
            
            {/* Payment-specific messages */}
            {isOnlinePayment && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-green-800">
                  ✅ Payment confirmed! Your order is being prepared.
                </p>
              </div>
            )}
            
            {isCashPayment && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-orange-800">
                  💰 Please have ₹{orderData.total.toFixed(2)} ready. Our staff will collect payment when your order is ready.
                </p>
              </div>
            )}
            
            <p className="text-sm text-gray-500">
              🔔 You'll receive real-time updates as your order progresses from preparation to ready for pickup!
            </p>
            
            {/* Quick action button */}
            <button
              onClick={() => {
                onClose()
                setOrderSuccess(false)
                setOrderData(null)
              }}
              className="mt-4 px-6 py-2 bg-black text-white rounded-lg text-sm hover:bg-gray-800 transition-colors"
            >
              Continue Tracking
            </button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    )
  }

  // If user is logged in, show minimal payment selection
  if (isLoggedIn) {
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="p-4 border-b flex items-center gap-3">
                <button onClick={onClose} className="p-2">
                  <ArrowLeftIcon className="w-5 h-5" style={{ color: DARK_TEXT }} />
                </button>
                <h3 className="text-base font-bold" style={{ color: DARK_TEXT }}>
                  Bill total: ₹{(cartSummary.total + selectedTip).toFixed(2)}
                </h3>
              </div>

              {/* Payment Options */}
              <div className="p-4">
                {/* Pay at Counter */}
                <div className="mb-6">
                  <h4 className="text-sm font-bold mb-3" style={{ color: DARK_TEXT }}>Recommended</h4>
                  <button
                    onClick={() => setSelectedPayment('cash')}
                    className={`w-full p-4 rounded-xl border-2 flex items-center justify-between transition-all ${
                      selectedPayment === 'cash' ? 'border-green-500 bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                        <BanknotesIcon className="w-6 h-6" style={{ color: ACTION_GREEN }} />
                      </div>
                      <span className="text-sm font-semibold" style={{ color: DARK_TEXT }}>Pay at Counter</span>
                    </div>
                    <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center" 
                      style={{ borderColor: selectedPayment === 'cash' ? ACTION_GREEN : '#E0E0E0' }}>
                      {selectedPayment === 'cash' && (
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ACTION_GREEN }}></div>
                      )}
                    </div>
                  </button>
                </div>

                {/* Pay Online - Cashfree */}
                <div className="mb-6">
                  <h4 className="text-sm font-bold mb-3" style={{ color: DARK_TEXT }}>Online Payment</h4>
                  <button
                    onClick={() => setSelectedPayment('online')}
                    disabled={!cashfreeLoaded}
                    className={`w-full p-4 rounded-xl border-2 flex items-center justify-between transition-all ${
                      selectedPayment === 'online' ? 'border-green-500 bg-green-50' : 'border-gray-200'
                    } ${!cashfreeLoaded ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                        <CreditCardIcon className="w-6 h-6" style={{ color: ACTION_GREEN }} />
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-semibold" style={{ color: DARK_TEXT }}>Pay Online</div>
                        <div className="text-xs" style={{ color: MEDIUM_GRAY }}>UPI, Cards, Wallets & More</div>
                      </div>
                    </div>
                    <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center" 
                      style={{ borderColor: selectedPayment === 'online' ? ACTION_GREEN : '#E0E0E0' }}>
                      {selectedPayment === 'online' && (
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ACTION_GREEN }}></div>
                      )}
                    </div>
                  </button>
                  {!cashfreeLoaded && (
                    <p className="text-xs mt-2" style={{ color: MEDIUM_GRAY }}>Loading payment gateway...</p>
                  )}
                </div>
              </div>

              {/* Place Order Button */}
              <div className="p-4 border-t">
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSubmit}
                  disabled={loading || processingPayment || (selectedPayment === 'online' && !cashfreeLoaded)}
                  className="w-full py-3.5 text-black font-bold text-sm uppercase rounded-xl disabled:opacity-50"
                  style={{ 
                    backgroundColor: '#00C853',
                    boxShadow: '0 4px 0 0 #000000'
                  }}
                >
                  {processingPayment ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      PROCESSING PAYMENT...
                    </span>
                  ) : loading ? 'PLACING ORDER...' : selectedPayment === 'online' ? 'PAY NOW' : 'PLACE ORDER'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    )
  }

  // For non-logged-in users, show full form
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-gray-50 rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
          style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
        >
          {/* Header - Brand Green */}
          <div className="px-6 py-4 flex items-center justify-between border-b border-gray-200" style={{ backgroundColor: BRAND_GREEN }}>
            <h3 className="text-xl font-black" style={{ color: DARK_TEXT }}>Complete Order</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-black/10 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-6 w-6" style={{ color: DARK_TEXT }} />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* Customer Details */}
            <div>
              <h4 className="font-bold text-sm mb-3" style={{ color: DARK_TEXT }}>Customer Details</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: MEDIUM_GRAY }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                    className="w-full px-4 py-2.5 bg-white border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none transition-colors text-sm font-medium"
                    style={{ color: DARK_TEXT }}
                    placeholder="Your name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: MEDIUM_GRAY }}>
                    Phone *
                  </label>
                  <input
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                    className="w-full px-4 py-2.5 bg-white border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none transition-colors text-sm font-medium"
                    style={{ color: DARK_TEXT }}
                    placeholder="Your phone number"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Special Instructions */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: MEDIUM_GRAY }}>
                Special Instructions
              </label>
              <textarea
                value={customerInfo.specialInstructions}
                onChange={(e) => setCustomerInfo({...customerInfo, specialInstructions: e.target.value})}
                className="w-full px-4 py-2.5 bg-white border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none transition-colors text-sm resize-none"
                style={{ color: DARK_TEXT }}
                rows={2}
                placeholder="Any special requests or dietary requirements?"
              />
            </div>

            {/* Payment Method */}
            <div>
              <h4 className="font-bold text-sm mb-3" style={{ color: DARK_TEXT }}>Payment Method</h4>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setCustomerInfo({...customerInfo, paymentMethod: 'cash'})}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    customerInfo.paymentMethod === 'cash'
                      ? 'border-black bg-white'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <BanknotesIcon className="h-5 w-5 mx-auto mb-1" style={{ color: DARK_TEXT }} />
                  <span className="block text-xs font-bold" style={{ color: DARK_TEXT }}>Cash</span>
                </button>
                <button
                  onClick={() => setCustomerInfo({...customerInfo, paymentMethod: 'online'})}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    customerInfo.paymentMethod === 'online'
                      ? 'border-black bg-white'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <CreditCardIcon className="h-5 w-5 mx-auto mb-1" style={{ color: DARK_TEXT }} />
                  <span className="block text-xs font-bold" style={{ color: DARK_TEXT }}>Online</span>
                </button>
              </div>
            </div>

            {/* Add Tip */}
            <div>
              <h4 className="font-bold text-sm mb-3" style={{ color: DARK_TEXT }}>Add Tip (Optional)</h4>
              <div className="grid grid-cols-3 gap-2">
                {tipAmounts.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setSelectedTip(selectedTip === amount ? 0 : amount)}
                    className={`py-2 rounded-xl border-2 transition-all text-xs font-bold ${
                      selectedTip === amount
                        ? 'border-black bg-white'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                    style={{ color: DARK_TEXT }}
                  >
                    ₹{amount}
                  </button>
                ))}
              </div>
              <p className="text-[10px] font-medium mt-2 flex items-center gap-1" style={{ color: '#15803D' }}>
                <span>💚</span> 100% of the tip goes to your service staff
              </p>
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-xl p-4 border-2 border-gray-200">
              <h4 className="font-bold text-sm mb-3" style={{ color: DARK_TEXT }}>Order Summary</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm" style={{ color: DARK_TEXT }}>
                  <span>Subtotal</span>
                  <span className="font-semibold">₹{subtotal.toFixed(0)}</span>
                </div>
                {itemSavings > 0 && (
                  <div className="flex justify-between text-sm font-bold" style={{ color: '#00C853' }}>
                    <span>💰 Savings with Ordyrr</span>
                    <span>-₹{itemSavings.toFixed(0)}</span>
                  </div>
                )}
                {discount > 0 && (
                  <div className="flex justify-between text-sm font-bold" style={{ color: '#15803D' }}>
                    <span>{appliedOffer ? appliedOffer.offer_name : 'First Order Discount'}</span>
                    <span>-₹{discount.toFixed(0)}</span>
                  </div>
                )}
                {coinsDiscount > 0 && (
                  <div className="flex justify-between text-sm font-bold" style={{ color: '#F59E0B' }}>
                    <span>Ordyrr Coins ({Math.floor(coinsUsed)})</span>
                    <span>-₹{coinsDiscount.toFixed(2)}</span>
                  </div>
                )}

                {selectedTip > 0 && (
                  <div className="flex justify-between text-sm" style={{ color: MEDIUM_GRAY }}>
                    <span>Tip for Staff</span>
                    <span className="font-semibold" style={{ color: DARK_TEXT }}>₹{selectedTip}</span>
                  </div>
                )}
                <div className="border-t-2 border-gray-200 pt-2 mt-2">
                  <div className="flex justify-between font-black text-lg" style={{ color: DARK_TEXT }}>
                    <span>Total</span>
                    <span>₹{(total + selectedTip).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons - Fixed at Bottom */}
          <div className="px-6 py-4 border-t border-gray-200 bg-white flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl font-bold text-sm hover:bg-gray-50 transition-colors"
              style={{ color: DARK_TEXT }}
            >
              Cancel
            </button>
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={handleSubmit}
              disabled={loading || processingPayment || (customerInfo.paymentMethod === 'online' && !cashfreeLoaded)}
              className="flex-1 px-4 py-3 text-black rounded-xl font-black text-sm uppercase disabled:opacity-50"
              style={{ 
                backgroundColor: ACTION_GREEN,
                boxShadow: '0 4px 0 0 #000000'
              }}
            >
              {processingPayment ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  PROCESSING PAYMENT...
                </span>
              ) : loading ? 'PLACING ORDER...' : customerInfo.paymentMethod === 'online' ? 'PAY NOW' : 'PLACE ORDER'}
            </motion.button>
          </div>
        </motion.div>

        {/* Celebratory Discount Popup - Minimal Design */}
        <AnimatePresence>
          {showDiscountPopup && discountAmount > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-sm"
              onClick={() => setShowDiscountPopup(false)}
            >
              {/* Outer Sparkles - Burst Effect */}
              {[...Array(30)].map((_, i) => {
                const angle = (i / 30) * 360
                const distance = 150 + Math.random() * 100
                return (
                  <motion.div
                    key={i}
                    initial={{ 
                      x: 0, 
                      y: 0, 
                      scale: 0, 
                      opacity: 0,
                      rotate: 0
                    }}
                    animate={{
                      x: Math.cos(angle * Math.PI / 180) * distance,
                      y: Math.sin(angle * Math.PI / 180) * distance,
                      scale: [0, 1.5, 1],
                      opacity: [0, 1, 0],
                      rotate: [0, 360]
                    }}
                    transition={{
                      duration: 1.5,
                      delay: i * 0.02,
                      ease: "easeOut"
                    }}
                    className="absolute"
                    style={{ pointerEvents: 'none' }}
                  >
                    <span className="text-yellow-400 text-2xl">✨</span>
                  </motion.div>
                )
              })}

              {/* Popup Card - Minimal Design */}
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                className="bg-white rounded-2xl p-6 max-w-xs w-full mx-4 relative z-10"
                style={{ boxShadow: '0 0 0 3px #000000' }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Simple Content */}
                <div className="text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  >
                    <span className="text-6xl block mb-3">🎉</span>
                  </motion.div>
                  
                  <h3 className="text-xl font-black mb-2" style={{ color: '#00C853' }}>
                    YAY!
                  </h3>
                  
                  <p className="text-sm mb-2 font-semibold" style={{ color: '#00C853' }}>
                    You saved
                  </p>
                  
                  <p className="text-4xl font-black mb-2" style={{ color: '#00C853' }}>
                    ₹{discountAmount.toFixed(0)}
                  </p>
                  
                  <p className="text-xs mb-5 font-medium" style={{ color: '#00C853' }}>
                    First order discount applied! 🎊
                  </p>
                  
                  {/* Playful Boxy Button */}
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (popupTimeout.current) {
                        clearTimeout(popupTimeout.current)
                      }
                      setShowDiscountPopup(false)
                    }}
                    className="w-full py-3.5 font-black text-black text-sm uppercase rounded-xl"
                    style={{ 
                      backgroundColor: '#00C853',
                      boxShadow: '0 4px 0 0 #000000'
                    }}
                  >
                    AWESOME! 🎉
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  )
}

export default CheckoutModal